import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { fetchThread, postReply, type ThreadComment } from '@/api/comments';
import { bumpCommentCount } from '@/utils/commentCountCache';

/**
 * comment-circle-scope — le fil d'une reco DANS UN CERCLE.
 *
 * Une conversation appartient à un cercle et n'existe que là. La carte du deck
 * portait ce fil en état local ; comme elle est identifiée par la reco seule
 * (`key={d.id}`), React réutilisait l'instance d'un onglet à l'autre et le fil
 * chargé pour le cercle #1 restait affiché dans le cercle #2.
 */
export function useCircleThread(recoId: number, circleId: number | null) {
  const [thread, setThread] = useState<ThreadComment[] | null>(null);
  const [loadedFor, setLoadedFor] = useState(circleId);
  const queryClient = useQueryClient();
  // Le cercle affiché À L'INSTANT où une réponse serveur arrive. Un `useState`
  // ne suffit pas : la closure du `.then` capturerait la valeur du rendu qui a
  // lancé la requête, c'est-à-dire précisément celle qu'on veut invalider. Même
  // motif que `onDataRef` dans `usePushNotifications` — la synchro est dans un
  // effet, écrire un ref pendant le rendu casse avec le rendu concurrent.
  const shownCircle = useRef(circleId);
  useEffect(() => {
    shownCircle.current = circleId;
  }, [circleId]);

  // Remise à zéro PENDANT LE RENDU, pas dans un `useEffect` : un effet laisse
  // passer une image où la feuille affiche encore le fil du cercle précédent.
  // Une image de fuite reste une fuite. C'est l'idiome React pour ajuster un
  // état quand une prop change.
  if (loadedFor !== circleId) {
    setLoadedFor(circleId);
    setThread(null);
  }

  const canReply = circleId != null;

  const load = useCallback(() => {
    // Sans cercle, il n'y a pas de conversation du tout — pas même vide. Le back
    // refuse d'y poster (403) : un fil vide inviterait à écrire ce qu'on refuse.
    if (circleId == null) return;
    // Un fil déjà chargé ne se recharge pas : ouvrir la feuille deux fois de
    // suite ne doit pas rappeler le serveur.
    if (thread !== null) return;
    void fetchThread(recoId, circleId).then((rows) => {
      // La requête a pu partir pour le cercle #7 et revenir alors que le
      // lecteur regarde le #9. La remise à zéro au rendu ne couvre que le cas
      // synchrone ; une réponse en retard la contournerait.
      if (shownCircle.current !== circleId) return;
      setThread(rows);
    });
  }, [recoId, circleId, thread]);

  const reply = useCallback(
    async (content: string) => {
      if (circleId == null) return;
      const created = await postReply(recoId, circleId, content);
      // Même course qu'au chargement : la confirmation peut revenir alors que le
      // lecteur regarde un autre cercle (une notification tapée déclenche
      // `landOnCircle` sans fermer la feuille). La réponse appartient bien au
      // cercle d'écriture — elle est postée, son compteur incrémenté plus bas —
      // mais elle n'a rien à faire dans le fil affiché d'à côté.
      if (shownCircle.current === circleId) {
        setThread((prev) => [...(prev ?? []), created]);
      }
      // La pastille lit `commentCount` dans le cache du feed : sans ça, elle ne
      // bougeait qu'au rechargement. Écriture directe, jamais un invalidate — le
      // feed est un `useInfiniteQuery`, le refetcher rebattrait le deck sous le
      // doigt. Et TOUJOURS avec le cercle : c'est son compteur, pas celui d'à côté.
      bumpCommentCount(queryClient, recoId, circleId);
    },
    [queryClient, recoId, circleId],
  );

  return { thread, canReply, load, reply };
}
